import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import AddToTargetListButton from '../../CollegeCoachActivity/components/AddToTargetListButton';
import GenericPopup from '../../../PopUps/GenericPopup';
import Img from '../../../Img';
import InfoButton from '../../../InfoButton';
import { ratingTypes } from '../../../../utils/constants';
import { getApiAxiosInstance } from '../../../../utils/network';
import { showErrorPopup } from '../../../../actions/notification-actions';
import {
    PowerFunnelGatedFeatures,
    CppPlayerActions,
} from '../../../Analytics/constants';
import useCollegeProspectAccess from '../../../hooks/useCollegeProspectAccess';
import useCPPTracking from '../../../hooks/useCPPTracking';
import { ApiEndpoints, Defaults, SearchParamNames, SearchTypeNames, SortDirections } from '../../../../api/searchApiConstants';
import paths from '../../../../../constants/paths';
import styles from '../collegeProspectTab.scss';

const TESTID_PREFIX = 'profile.college-prospect.college-fit-tool';
const COLLEGES_SEARCH_ENDPOINT = `${ApiEndpoints.SEARCH_BASE}/${SearchTypeNames.COLLEGES}`;
const SUGGESTED_COLLEGES_LIMIT = 5;
// Fetch a deeper pool than we display so colleges already on the target list can be
// skipped and backfilled — the card always offers SUGGESTED_COLLEGES_LIMIT addable options.
const SUGGESTED_COLLEGES_FETCH_LIMIT = 20;
const TEAM_TOP_SIX_POSITION = '6';
const DEFAULT_FIT_RATING = 16;
const SUGGESTED_COLLEGES_SORT_FIELD = 'school.power6';
const SUGGESTED_COLLEGES_SEARCH_ORIGIN = 'searchPage';
const LOCK_ICON_SRC = '/img/v4/lock.svg';
const INFO_TOOLTIP_CONTENT = (
    <div>
        <p data-testid={`${TESTID_PREFIX}.tooltip.intro`}>
            The College Fit Tool finds the highest-level programs where your {ratingTypes.tennis} would slot into a team&apos;s lineup, so you can see where you&apos;d realistically compete.
        </p>
        <ul>
            <li>
                <strong>{ratingTypes.tennis}:</strong> We pre-fill your current verified singles rating. Enter a different value to explore what-if scenarios at another rating.
            </li>
            <li>
                <strong>Lineup Range:</strong> The roster spot you want to fit into (e.g. Top 6). We match schools where your rating would place you within that range.
            </li>
        </ul>
        <p data-testid={`${TESTID_PREFIX}.tooltip.apply-hint`}>
            Hit Apply to open the full college search with these filters applied.
        </p>
    </div>
);
const SEARCH_TYPE_PARAM = 'type';
const COLLEGE_TEAMS_PATH = `${paths.collegeCorner}/teams`;
const DEFAULT_COLLEGE_IMAGE = '/img/v2/icons/icon-avatar-college.svg';
const GENDER_PARAM_MALE = 'M';
const GENDER_PARAM_FEMALE = 'F';

const resolveGenderParam = (gender) => {
    if (typeof gender !== 'string') {
        return null;
    }
    const normalized = gender.trim().toUpperCase();
    if (normalized === 'M' || normalized === 'MALE') {
        return GENDER_PARAM_MALE;
    }
    if (normalized === 'F' || normalized === 'FEMALE') {
        return GENDER_PARAM_FEMALE;
    }
    return null;
};

const LINEUP_POSITIONS = [
    { value: '1', label: 'Top 1' },
    { value: '2', label: 'Top 2' },
    { value: '3', label: 'Top 3' },
    { value: '4', label: 'Top 4' },
    { value: '5', label: 'Top 5' },
    { value: '6', label: 'Top 6' },
];

const mapHitToSuggestedCollege = (hit) => {
    const club = hit?.source || hit || {};
    const p6Rating = club.activeRoster?.power6?.power6Rating ??
        club.school?.power6 ??
        0;
    return {
        id: club.id,
        name: club.name || club.displayName || '',
        location: club.location?.display || '',
        profileImages: club.clubProfileImages,
        power6Rating: Number(p6Rating) || 0,
    };
};

const CollegeFitTool = ({ onAddSuccess, addedClubIds }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { currentUtr, currentUtrDisplay } = useSelector((state) => {
        const claimedPlayer = state.auth?.user?.claimedPlayer;
        return {
            currentUtr: claimedPlayer?.ratingSingles ?? claimedPlayer?.singlesUtr,
            currentUtrDisplay: claimedPlayer?.singlesUtrDisplay,
        };
    });
    const userGender = useSelector((state) => state.auth?.user?.gender);
    const { isPowered, hasAnnualPower, openUpgradePopup } = useCollegeProspectAccess();
    const isLocked = !hasAnnualPower;
    // The Apply / fit-search action is available to any Power member (monthly or annual); only
    // free members get the locked treatment. The suggested-colleges list above stays annual-only.
    const isApplyLocked = !isPowered;
    const { trackPlayerEngagement } = useCPPTracking();
    const genderParam = resolveGenderParam(userGender);
    const numericUtr = Number(currentUtr);
    const hasCurrentUtr = Number.isFinite(numericUtr) && numericUtr > 0;

    // Prefer the server-formatted display value used by the profile header
    // (singlesUtrDisplay, e.g. "11.30") so the field is guaranteed to match the header.
    // Fall back to a 2-decimal formatting of the raw rating for legacy payloads.
    const displayUtr = hasCurrentUtr
        ? (currentUtrDisplay || numericUtr.toFixed(2))
        : '';
    const [utrInput, setUtrInput] = useState(displayUtr);
    const [lineupPosition, setLineupPosition] = useState(TEAM_TOP_SIX_POSITION);
    const [suggestedColleges, setSuggestedColleges] = useState([]);
    const [addError, setAddError] = useState(null);

    useEffect(() => {
        if (hasCurrentUtr) {
            setUtrInput(displayUtr);
        }
    }, [hasCurrentUtr, displayUtr]);

    const fetchSuggestedColleges = useCallback(async () => {
        // Suggested colleges are recommended off the player's UTR; with no rating there is nothing
        // to recommend, so skip the fetch and let the Apply form take the full width.
        if (!hasCurrentUtr) {
            setSuggestedColleges([]);
            return;
        }
        const params = {
            [SearchParamNames.TOP]: SUGGESTED_COLLEGES_FETCH_LIMIT,
            [SearchParamNames.SKIP]: Defaults.DEFAULT_SKIP,
            [SearchParamNames.UTR_FIT_RATING]: numericUtr,
            [SearchParamNames.UTR_FIT_POSITION]: TEAM_TOP_SIX_POSITION,
            [SearchParamNames.SCHOOL_CLUB_SEARCH]: Defaults.SCHOOL_CLUB_SEARCH_VALUE,
            [SearchParamNames.SORT]: `${SUGGESTED_COLLEGES_SORT_FIELD}:${SortDirections.DESC}`,
            [SearchParamNames.SEARCH_ORIGIN]: SUGGESTED_COLLEGES_SEARCH_ORIGIN,
        };
        if (genderParam) {
            params[SearchParamNames.GENDER] = genderParam;
        }
        try {
            const response = await getApiAxiosInstance().get(COLLEGES_SEARCH_ENDPOINT, { params });
            const hits = response.data?.hits || [];
            setSuggestedColleges(hits.map(mapHitToSuggestedCollege).filter(college => college.id));
        } catch (err) {
            dispatch(showErrorPopup({
                message: 'We couldn\'t load suggested colleges. Please try again.',
                error: err,
            }));
            setSuggestedColleges([]);
        }
    }, [hasCurrentUtr, numericUtr, genderParam, dispatch]);

    useEffect(() => {
        fetchSuggestedColleges();
    }, [fetchSuggestedColleges]);

    const buildFitSearchUrl = (rating, position) => {
        const search = new URLSearchParams();
        search.append(SEARCH_TYPE_PARAM, SearchTypeNames.COLLEGES);
        if (rating) {
            search.append(SearchParamNames.UTR_FIT_RATING, rating);
        }
        search.append(SearchParamNames.UTR_FIT_POSITION, position);
        if (genderParam) {
            search.append(SearchParamNames.GENDER, genderParam);
        }
        return `/search?${search.toString()}`;
    };

    // Prefer the server-formatted display value (singlesUtrDisplay, e.g. "11.30") so
    // the URL parameter matches the UTR shown on the CPP screen and profile header.
    // Fall back to a 2-decimal formatting of the raw rating for legacy payloads.
    const viewAllFitRating = hasCurrentUtr
        ? (currentUtrDisplay || numericUtr.toFixed(2))
        : DEFAULT_FIT_RATING;
    const viewAllFitSearchUrl = buildFitSearchUrl(viewAllFitRating, TEAM_TOP_SIX_POSITION);

    const buildCollegeTeamsUrl = (rating, position) => {
        const search = new URLSearchParams();
        if (rating) {
            search.append(SearchParamNames.UTR_FIT_RATING, rating);
        }
        search.append(SearchParamNames.UTR_FIT_POSITION, position);
        return `${COLLEGE_TEAMS_PATH}?${search.toString()}`;
    };

    const handleApply = () => {
        trackPlayerEngagement(CppPlayerActions.CPP_COLLEGE_FIT_USED);
        history.push(buildCollegeTeamsUrl(utrInput, lineupPosition));
    };

    const handleAddError = (message) => {
        setAddError(message);
    };

    const handleCloseAddError = () => {
        setAddError(null);
    };

    const handleUnlock = () => {
        openUpgradePopup(PowerFunnelGatedFeatures.CPP_COLLEGE_FIT_LOCKED);
    };

    const eligibleSuggestedColleges = suggestedColleges
        .filter((college) => !addedClubIds?.has(college.id))
        .slice(0, SUGGESTED_COLLEGES_LIMIT)
        .sort((a, b) => b.power6Rating - a.power6Rating);
    const hasSuggestedColleges = eligibleSuggestedColleges.length > 0;
    const suggestedCollegesSubText = `These are the highest-level schools where your current ${ratingTypes.tennis} would place you in the team's top six.`;

    const renderSuggestedCollegeRow = (college) => {
        const icon = college.profileImages?.icon;
        const logoSrc = college.profileImages?.default
            ? `/v1/club/${college.profileImages.default}`
            : DEFAULT_COLLEGE_IMAGE;
        const logoSrcSet = icon?.oneX && icon?.twoX && icon?.threeX
            ? `/v1/club/${icon.oneX} 1x, /v1/club/${icon.twoX} 2x, /v1/club/${icon.threeX} 3x`
            : undefined;

        return (
            <div
                key={college.id}
                className={styles.suggestedCollegeRow}
                data-testid={`${TESTID_PREFIX}.suggested.college-${college.id}`}
            >
                <Link
                    to={`/colleges/${college.id}`}
                    className={styles.suggestedCollegeLink}
                    data-testid={`${TESTID_PREFIX}.suggested.college-${college.id}.link`}
                >
                    <span className={styles.suggestedCollegeLogo}>
                        <Img
                            src={logoSrc}
                            srcSet={logoSrcSet}
                            defaultimg={DEFAULT_COLLEGE_IMAGE}
                            alt={college.name}
                            className={styles.suggestedCollegeLogoImg}
                        />
                    </span>
                    <span className={styles.suggestedCollegeName} data-testid={`${TESTID_PREFIX}.suggested.college-${college.id}.name`}>
                        {college.name}
                    </span>
                </Link>
                {isLocked ? (
                    <span
                        className={styles.suggestedCollegeLockIcon}
                        data-testid={`${TESTID_PREFIX}.suggested.college-${college.id}.lock`}
                    >
                        <Img src={LOCK_ICON_SRC} alt="Locked" />
                    </span>
                ) : (
                    <AddToTargetListButton
                        clubId={college.id}
                        collegeName={college.name}
                        addedClubIds={addedClubIds}
                        onSuccess={onAddSuccess}
                        onError={handleAddError}
                        data-testid={`${TESTID_PREFIX}.suggested.college-${college.id}.add-btn`}
                    />
                )}
            </div>
        );
    };

    return (
        <div className={styles.collegeFitTool} data-testid={`${TESTID_PREFIX}.container`}>
            <div className={styles.collegeFitToolHeader}>
                <div className={styles.collegeFitToolTitle} data-testid={`${TESTID_PREFIX}.title`}>
                    College Fit Tool
                </div>
                <InfoButton
                    hoverText={INFO_TOOLTIP_CONTENT}
                    small
                    data-testid={`${TESTID_PREFIX}.info`}
                />
            </div>

            <div className={styles.collegeFitToolCard}>
                <div className={styles.collegeFitToolColumns}>
                    <div className={styles.suggestedColleges} data-testid={`${TESTID_PREFIX}.suggested`}>
                        <div className={styles.suggestedCollegesHeader}>
                            <span className={styles.suggestedCollegesTitle} data-testid={`${TESTID_PREFIX}.suggested.title`}>
                                Suggested Colleges
                            </span>
                            {hasSuggestedColleges ? (
                                <Link
                                    to={viewAllFitSearchUrl}
                                    className={styles.suggestedCollegesViewAll}
                                    data-testid={`${TESTID_PREFIX}.suggested.view-all-link`}
                                >
                                    View All
                                </Link>
                            ) : (
                                <span
                                    className={`${styles.suggestedCollegesViewAll} ${styles.suggestedCollegesViewAllDisabled}`}
                                    aria-disabled="true"
                                    data-testid={`${TESTID_PREFIX}.suggested.view-all-link`}
                                >
                                    View All
                                </span>
                            )}
                        </div>
                        {hasSuggestedColleges ? (
                            <>
                                <p className={styles.suggestedCollegesSubText} data-testid={`${TESTID_PREFIX}.suggested.subtext`}>
                                    {suggestedCollegesSubText}
                                </p>
                                <div className={styles.suggestedCollegesListHeader}>
                                    <span className={styles.suggestedCollegesAddLabel} data-testid={`${TESTID_PREFIX}.suggested.add-label`}>
                                        Add to List
                                    </span>
                                </div>
                                {isLocked ? (
                                    <div
                                        className={styles.suggestedCollegesLockedWrapper}
                                        role="button"
                                        tabIndex={0}
                                        onClick={handleUnlock}
                                        onKeyPress={handleUnlock}
                                        data-testid={`${TESTID_PREFIX}.suggested.locked`}
                                    >
                                        <div className={styles.suggestedCollegesLocked} aria-hidden="true">
                                            <div className={styles.suggestedCollegesList}>
                                                {eligibleSuggestedColleges.map(renderSuggestedCollegeRow)}
                                            </div>
                                        </div>
                                        <span className={styles.suggestedCollegesLockOverlay}>
                                            <Img
                                                src={LOCK_ICON_SRC}
                                                alt=""
                                                className={styles.suggestedCollegesLockOverlayIcon}
                                            />
                                        </span>
                                    </div>
                                ) : (
                                    <div className={styles.suggestedCollegesList}>
                                        {eligibleSuggestedColleges.map(renderSuggestedCollegeRow)}
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className={styles.suggestedCollegesSubText} data-testid={`${TESTID_PREFIX}.suggested.empty-text`}>
                                Keep playing&mdash;we&apos;ll suggest colleges once you have enough verified results and a Verified Singles {ratingTypes.tennis} that places you in a team&apos;s top six.
                            </p>
                        )}
                    </div>

                    <div className={styles.collegeFitToolForm} data-testid={`${TESTID_PREFIX}.form`}>
                        <div className={styles.collegeFitToolFormTitle} data-testid={`${TESTID_PREFIX}.form-title`}>
                            Explore Colleges
                        </div>
                        <p className={styles.collegeFitToolPrompt} data-testid={`${TESTID_PREFIX}.prompt`}>
                            Find colleges based on {ratingTypes.tennis} and lineup positions.
                        </p>
                        <label className={styles.collegeFitToolLabel} htmlFor={`${TESTID_PREFIX}.utr-input`} data-testid={`${TESTID_PREFIX}.utr-label`}>
                            {ratingTypes.tennis}
                        </label>
                        <input
                            id={`${TESTID_PREFIX}.utr-input`}
                            type="text"
                            className="form-control"
                            placeholder={`Enter Your ${ratingTypes.tennis}`}
                            value={utrInput}
                            onChange={(e) => setUtrInput(e.target.value)}
                            data-testid={`${TESTID_PREFIX}.utr-input`}
                        />
                        <select
                            className={`form-control ${styles.collegeFitToolSelect}`}
                            value={lineupPosition}
                            onChange={(e) => setLineupPosition(e.target.value)}
                            data-testid={`${TESTID_PREFIX}.lineup-select`}
                        >
                            {LINEUP_POSITIONS.map((option) => (
                                <option value={option.value} key={option.value}>{option.label}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className={isApplyLocked
                                ? `${styles.collegeFitToolApply} ${styles.collegeFitToolApplyLocked}`
                                : styles.collegeFitToolApply}
                            onClick={isApplyLocked ? handleUnlock : handleApply}
                            data-testid={`${TESTID_PREFIX}.apply-btn`}
                        >
                            Apply
                            {isApplyLocked && (
                                <Img
                                    src={LOCK_ICON_SRC}
                                    alt=""
                                    className={styles.collegeFitToolApplyLockIcon}
                                />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {addError && (
                <GenericPopup
                    variant="cpp"
                    title="Couldn't Add College"
                    subtitle={addError}
                    buttonText="Okay"
                    onButtonClick={handleCloseAddError}
                    onRequestClose={handleCloseAddError}
                    data-testid="modal.college-fit-tool.add-college-error"
                />
            )}
        </div>
    );
};

CollegeFitTool.defaultProps = {
    onAddSuccess: null,
    addedClubIds: null,
};

CollegeFitTool.propTypes = {
    onAddSuccess: PropTypes.func,
    addedClubIds: PropTypes.instanceOf(Set),
};

export default CollegeFitTool;
